import { IconButton, Tooltip, Typography } from "@mui/material"
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import EditIcon from '@mui/icons-material/Edit';
import { Document, DocumentAdd, DocumentEdit } from "../../../../../models/document";
import { FieldValues, Path, UseFormReturn } from "react-hook-form";
import SettingsIcon from '@mui/icons-material/Settings';
import { DocumentStructure } from "../../../../../models/document-structure";
import MuiInput from "../../../../../components/mui-input/MuiInput";
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';


// theme
import { useTheme } from "@emotion/react";
import { Account } from "../../../../../models/account";
import { useLocation, useNavigate } from "react-router-dom";
import { DocumentSettings } from "../../../../../models/document-settings";
import { ItemsFormType, PaymentsFormType, WorksFormType } from "../../add-document/AddDocument.types";
import { DocumentAccountAdd } from "../../../../../models/document-account";

//state properties:
interface LocationState {

    reference?: string | null;
    customer?: Account | null;
    document?: Document | null;
}

interface DocumentBankProps {

    method: "add" | "edit";

    documentForm: UseFormReturn<DocumentAdd> | UseFormReturn<DocumentEdit>;
    worksForm: UseFormReturn<WorksFormType>
    itemsForm: UseFormReturn<ItemsFormType>
    paymentsForm: UseFormReturn<PaymentsFormType>

    settings: DocumentSettings;
    structure: DocumentStructure[];

    isDocumentClosed?: boolean;
}




function DocumentBank({ method, documentForm, worksForm, itemsForm, paymentsForm, settings, structure, isDocumentClosed }: DocumentBankProps) {

    const navigate = useNavigate();
    const { state, pathname } = useLocation() as { state: LocationState; pathname: string }; // state data from another page

    const theme = useTheme();

    function renderAccountField<T extends FieldValues>(field: DocumentStructure, documentForm: UseFormReturn<T>) {

        const isAccountNumberField = field.field_name === "customer.account_number";

        const shouldDisableCustomerAccountField = state?.reference || (!settings?.display_items && !settings?.display_works && settings?.display_payments && documentForm.getValues("customer.account_number" as Path<T>));
        const isAccountField = field.type === "חשבון" && field.show_form;

        if (isAccountField && isAccountNumberField && shouldDisableCustomerAccountField) {
            return <MuiInput key={field.field_name} field={field} variant='outlined' useForm={documentForm} showRequired disabled />;
        }


        if (isAccountField) {
            // return <MuiInput key={field.field_name} field={field} variant='outlined' useForm={documentForm} showRequired disabled={isDocumentClosed}/>;
            // return <AccountSearch
            //     key={field.field_name}
            //     accountHebType={"בנק"}
            //     fieldStructure={field}
            //     useForm={documentForm}
            //     prefixPath="document_account"
            //     onSelect={onBankSelect}
            //     disabled={isDocumentClosed}/>
            return <MuiInput
                key={field.field_name}
                field={field}
                variant='outlined'
                useForm={documentForm}
                prefix="document_account"
                showRequired
                disabled={isDocumentClosed}
                searchType="בנק"
                onSelect={onBankSelect}
            />;
        }

        // Return null if none of the conditions are met
        return null;
    }


    function createState(){
        return {
            ...state,
            from: pathname,
            document_settings_id: settings?.id,

            document_account: documentForm.getValues().document_account,
            car: documentForm.getValues().car,

            works: worksForm.getValues().works,
            items: itemsForm.getValues().items,
            payments: paymentsForm.getValues().payments,
            reference: documentForm.getValues().reference,
        }
    }

    
    function createBreadcrumbData(state: any){
        return [
            {
                name: "מסמכים",
                link: "/documents",
            },
            {
                name: "הוספת מסמך",
            },
            {
                name: `${settings?.name}`,
                link: `/documents/add?documentType=${settings?.id}`,
                state: state
            },
        ];
    }

    //navigate to bank browsing
    function browseBank() {

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState);

        navigate(`/banks`, {
            replace: true,
            state: {
                ...currentState,
                breadcrumbData: [...breadcrumbData, { name: "בחירת בנק" }]
            }
        });

    }


    // *********************************************************************************************


    //navigate to add customer
    function addBank() {

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState);

        navigate(`/banks/add`, {
            replace: true,
            state: {
                ...currentState,
                breadcrumbData: [...breadcrumbData, { name: "הוספת כרטיס בנק" }]
            }
        });
    }


    //navigate to add bank
    function editBank() {

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState);

        navigate(`/banks/${documentForm.getValues().document_account?.account_id || documentForm.getValues().document_account?.id}`, {
            replace: true,
            state: {
                ...currentState,
                breadcrumbData: [...breadcrumbData, { name: "עריכת כרטיס בנק" }]
            }
        });
    }


    function editBankStructure(){

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState);


        navigate(`/documents/structure/account?documentType=${settings.id}`, {
            replace: true,
            state: {
                breadcrumbData: [...breadcrumbData, { name: "הגדרת מבנה" }, { name: "בנק" }]
            }
        });
    }

    function onBankSelect(bank: Account){

        const documentBank: DocumentAccountAdd = {...bank, id: undefined, account_id: bank.id};
        
        console.log("on select documentBank", documentBank);

        documentForm.reset({
            ...documentForm.getValues(),
            document_account: documentBank
        });
    }

    /**
     * clean the Bank fields
     */
    function cleanFields(){
        documentForm.reset({
            ...documentForm.getValues(),
            document_account: null
        });
    }


    if (method === "add") {
        return (
            <>
                <div className="title">
                    <div>
                        <Typography variant="body1" component="span">
                            פרטי בנק:
                        </Typography>

                        <Tooltip title="בחירת כרטיס בנק מתוך דפדוף" placement="top">
                            <IconButton onClick={browseBank} sx={{ color: theme.colors.main_color }}>
                                <AutoStoriesIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="הוספת כרטיס בנק" placement="top">
                            <span>
                                <IconButton onClick={addBank} disabled={!!documentForm.getValues()?.document_account?.account_id} sx={{ color: theme.colors.main_color }}>
                                    <BookmarkAddIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="עריכת כרטיס בנק" placement="top">
                            <span>
                                <IconButton onClick={editBank} disabled={!documentForm.getValues()?.document_account?.account_id} sx={{ color: theme.colors.main_color }}>
                                    <EditIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="איפוס שדות" placement="top">
                            <span>
                                <IconButton onClick={cleanFields} sx={{ color: theme.colors.main_color }}>
                                    <DeleteSweepIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </div>
                    <div>
                        <Tooltip title="הגדרת שדות בנק" placement="top">
                            <IconButton onClick={editBankStructure} sx={{ color: theme.colors.main_color }}>
                                <SettingsIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>
                
                <div className="inputs">
                    {structure?.sort((a, b) => (a.location || 0) - (b.location || 0)).map(field => renderAccountField(field, documentForm as UseFormReturn<DocumentAdd>))}

                </div>
            </>
        )
    }

    if (method === "edit") {
        return (
            <>
                <div className="title">
                    <div>
                        <Typography variant="body1" component="span">
                            פרטי בנק:
                        </Typography>

                        <Tooltip title="בחירת כרטיס בנק מתוך דפדוף" placement="top">
                            <span>
                                <IconButton onClick={browseBank} sx={{ color: theme.colors.main_color }} disabled={isDocumentClosed}>
                                    <AutoStoriesIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="הוספת כרטיס בנק" placement="top">
                            <span>
                                <IconButton onClick={addBank} disabled={!!state?.customer?.id || !!state?.document?.document_account?.account_id || isDocumentClosed} sx={{ color: theme.colors.main_color }}>
                                    <BookmarkAddIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="עריכת כרטיס בנק" placement="top">
                            <span>
                                <IconButton onClick={editBank} disabled={(!state?.customer?.id && !state?.document?.document_account?.account_id) || isDocumentClosed} sx={{ color: theme.colors.main_color }}>
                                    <EditIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="איפוס שדות" placement="top">
                            <span>
                                <IconButton onClick={cleanFields} disabled={isDocumentClosed} sx={{ color: theme.colors.main_color }}>
                                    <DeleteSweepIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </div>
                    <div>
                        <Tooltip title="הגדרת שדות בנק" placement="top">
                            <IconButton onClick={editBankStructure} sx={{ color: theme.colors.main_color }}>
                                <SettingsIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>

                <div className="inputs">
                    {structure?.sort((a, b) => (a.location || 0) - (b.location || 0)).map(field => renderAccountField(field, documentForm as UseFormReturn<DocumentEdit>))}

                </div>
            </>
        )
    }

}

export default DocumentBank